import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Switch,
  FormControlLabel
} from '@mui/material';
import { Add, Refresh } from '@mui/icons-material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../constants/roles';

const AccessLogModule = () => {
  const { showToast } = useToast();
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [filters, setFilters] = useState({ storageArea: '', accessType: 'ALL', incidentOnly: false });
  const [dateFilters, setDateFilters] = useState({ start: '', end: '' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    storageArea: '',
    accessType: 'ENTRY',
    purpose: '',
    itemsAccessed: '',
    securityIncident: false,
    incidentDetails: ''
  });
  const canLog = [ROLES.STORE_INSPECTOR, ROLES.STORE_EXECUTIVE, ROLES.ACCOUNTABLE_MANAGER, ROLES.AM_MRO, ROLES.SUPER_ADMIN].includes(user?.role);

  const fetchLogs = useCallback(async () => {
    try {
      const params = {};
      if (filters.storageArea) params.storageArea = filters.storageArea;
      if (filters.accessType !== 'ALL') params.accessType = filters.accessType;
      if (filters.incidentOnly) params.securityIncident = true;
      if (dateFilters.start) params.startDate = dateFilters.start;
      if (dateFilters.end) params.endDate = dateFilters.end;
      const response = await axios.get('/api/store/access-log', { params });
      setLogs(response.data || []);
    } catch (error) {
      console.error('Failed to load access logs', error);
      showToast('Failed to load access logs', 'error');
    }
  }, [filters, dateFilters, showToast]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const filteredLogs = useMemo(() => logs, [logs]);

  const handleDialogClose = () => {
    setDialogOpen(false);
    setFormData({
      storageArea: '',
      accessType: 'ENTRY',
      purpose: '',
      itemsAccessed: '',
      securityIncident: false,
      incidentDetails: ''
    });
  };

  const handleCreate = async () => {
    if (!formData.storageArea) {
      showToast('Storage area is required', 'error');
      return;
    }
    try {
      const payload = {
        ...formData,
        itemsAccessed: formData.itemsAccessed
          ? formData.itemsAccessed.split(',').map((item) => item.trim()).filter(Boolean)
          : []
      };
      await axios.post('/api/store/access-log', payload);
      showToast('Access log recorded', 'success');
      handleDialogClose();
      fetchLogs();
    } catch (error) {
      console.error('Failed to record access log', error);
      showToast(error.response?.data?.message || 'Failed to record access log', 'error');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Store Access & Security Logs</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchLogs}>
              <Refresh />
            </IconButton>
          </Tooltip>
          {canLog && (
            <Button variant="contained" startIcon={<Add />} onClick={() => setDialogOpen(true)}>
              Log Entry
            </Button>
          )}
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Storage Area"
              size="small"
              value={filters.storageArea}
              onChange={(e) => setFilters({ ...filters, storageArea: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Access Type</InputLabel>
              <Select
                value={filters.accessType}
                label="Access Type"
                onChange={(e) => setFilters({ ...filters, accessType: e.target.value })}
              >
                <MenuItem value="ALL">All</MenuItem>
                <MenuItem value="ENTRY">Entry</MenuItem>
                <MenuItem value="EXIT">Exit</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={filters.incidentOnly}
                  onChange={(e) => setFilters({ ...filters, incidentOnly: e.target.checked })}
                />
              }
              label="Show incidents only"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              size="small"
              label="Start Date"
              InputLabelProps={{ shrink: true }}
              value={dateFilters.start}
              onChange={(e) => setDateFilters({ ...dateFilters, start: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              size="small"
              label="End Date"
              InputLabelProps={{ shrink: true }}
              value={dateFilters.end}
              onChange={(e) => setDateFilters({ ...dateFilters, end: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <Button variant="outlined" onClick={fetchLogs}>
              Apply Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Access Date</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Storage Area</TableCell>
              <TableCell>Purpose</TableCell>
              <TableCell>Items Accessed</TableCell>
              <TableCell>Incident</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredLogs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    No access logs found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredLogs.map((log) => (
                <TableRow key={log._id}>
                  <TableCell>
                    {log.accessDate ? new Date(log.accessDate).toLocaleString() : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={log.accessType}
                      size="small"
                      color={log.accessType === 'ENTRY' ? 'success' : 'info'}
                    />
                  </TableCell>
                  <TableCell>{log.storageArea}</TableCell>
                  <TableCell>{log.purpose || '—'}</TableCell>
                  <TableCell>
                    {log.itemsAccessed && log.itemsAccessed.length > 0
                      ? log.itemsAccessed.join(', ')
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {log.securityIncident ? (
                      <Chip label="Incident" color="error" size="small" />
                    ) : (
                      <Chip label="Normal" size="small" />
                    )}
                    {log.incidentDetails && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {log.incidentDetails}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Record Access Log</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Storage Area"
                value={formData.storageArea}
                onChange={(e) => setFormData({ ...formData, storageArea: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Access Type</InputLabel>
                <Select
                  value={formData.accessType}
                  label="Access Type"
                  onChange={(e) => setFormData({ ...formData, accessType: e.target.value })}
                >
                  <MenuItem value="ENTRY">Entry</MenuItem>
                  <MenuItem value="EXIT">Exit</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Purpose"
                value={formData.purpose}
                onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Items Accessed (comma separated)"
                value={formData.itemsAccessed}
                onChange={(e) => setFormData({ ...formData, itemsAccessed: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.securityIncident}
                    onChange={(e) => setFormData({ ...formData, securityIncident: e.target.checked })}
                  />
                }
                label="Security Incident"
              />
            </Grid>
            {formData.securityIncident && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Incident Details"
                  multiline
                  rows={2}
                  value={formData.incidentDetails}
                  onChange={(e) => setFormData({ ...formData, incidentDetails: e.target.value })}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained">
            Log Access
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccessLogModule;

